import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { FlowpilotIconComponent } from '@core/components/sidebar/components/flowpilot/icon/flowpilot-icon.component';
import { FlowpilotWindowComponent } from '@core/components/sidebar/components/flowpilot/window/flowpilot-window.component';
import {
  Bounds,
  Position,
} from '@core/components/sidebar/components/flowpilot/window/flowpilot-window-layout.component';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { KeyboardService } from '@core/services/keyboard.service';
import { KeyCode } from '@core/enums/key-parameters';
import { WindowContentService } from '@core/components/sidebar/components/flowpilot/window/services/window-content.service';
import { provideFlowpilotServices } from '@core/components/sidebar/components/flowpilot/window/services/provide-flowpilot-services';

type FlowpilotWindowState = 'closed' | 'open' | 'closing' | 'opening';

@Component({
  selector: 'app-flowpilot',
  imports: [NgStyle, FlowpilotIconComponent, FlowpilotWindowComponent, NgClass],
  providers: [provideFlowpilotServices()],
  template: `
    <div
      class="flex w-full items-center gap-[13px]"
      [class.invisible]="state() === 'open'"
      [class.cursor-pointer]="state() === 'closed'"
      [class.pointer-events-none]="state() === 'open' || state() === 'opening'"
      (click)="openWindow()">
      <div #iconAnchor class="size-10 shrink-0 rounded-full"></div>

      @if (!collapsed() && state() === 'closed') {
        <span
          class="text--contrast-high pb-0.5 text-sm font-medium text-nowrap select-none">
          Flow Pilot
        </span>
      }
    </div>

    <div
      #morphEl
      class="fixed z-500 overflow-hidden"
      [ngStyle]="morphElStyles()"
      [style.background-image]="backgroundGradient">
      <div class="absolute inset-0" [ngClass]="iconContainerClasses">
        @if (state() !== 'open') {
          <app-flowpilot-icon (clicked)="openWindow()" />
        }
      </div>

      <div
        class="absolute inset-0 transition-opacity"
        [ngClass]="windowContainerClasses">
        @if (state() !== 'closed') {
          <app-flowpilot-window
            (collapse)="collapseWindow()"
            (windowMove)="onMove($event)"
            (resizeMove)="onResize($event)">
          </app-flowpilot-window>
        }
      </div>
    </div>
  `,
})
export class FlowpilotComponent implements AfterViewInit {
  /** INJECTIONS **/
  /****/
  private readonly windowContentService = inject(WindowContentService);

  /****/
  private readonly keyboardService = inject(KeyboardService);

  /** INPUTS **/
  /****/
  readonly collapsed = input.required<boolean>();

  /** OUTPUTS **/
  /****/
  readonly opened = output<void>();

  /****/
  readonly closed = output<void>();

  /** PROPERTIES **/
  /****/
  private readonly _anchor =
    viewChild.required<ElementRef<HTMLDivElement>>('iconAnchor');

  /****/
  private readonly _morphEl =
    viewChild.required<ElementRef<HTMLDivElement>>('morphEl');

  /****/
  protected state = signal<FlowpilotWindowState>('closed');

  /****/
  protected morphElStyles = computed(() => {
    return {
      transition: this._transitionStyle(),
      ['transition-delay']: this._transitionDelayStyle(),
      borderRadius: this._borderRadiusStyle(),
      boxShadow: this._boxShadowStyle(),
      left: this._leftStyle(),
      top: this._topStyle(),
      width: `${this._widthStyle()}px`,
      height: `${this._heightStyle()}px`,
    };
  });

  /****/
  private readonly _transitionStyle = signal<string>('none');

  /****/
  private readonly _transitionDelayStyle = signal<string>('0ms');

  /****/
  private readonly _borderRadiusStyle = signal<string>('50%');

  /****/
  private readonly _boxShadowStyle = signal<string>('none');

  /****/
  private readonly _leftStyle = signal<string>('0');

  /****/
  private readonly _topStyle = signal<string>('0');

  /****/
  private readonly _widthStyle = signal<number>(0);

  /****/
  private readonly _heightStyle = signal<number>(0);

  /****/
  private readonly INITIAL_OPEN_WIDTH = 400;

  /****/
  private readonly INITIAL_OPEN_HEIGHT = 540;

  /****/
  private readonly _openingTransition =
    'left 0.45s cubic-bezier(0.34,1.4,0.64,1),' +
    'top 0.45s cubic-bezier(0.34,1.4,0.64,1),' +
    'width 0.45s cubic-bezier(0.34,1.4,0.64,1),' +
    'height 0.45s cubic-bezier(0.34,1.4,0.64,1),' +
    'border-radius 0.4s ease-out,' +
    'box-shadow 0.3s ease';

  /****/
  private readonly _closingTransition =
    'left 0.38s ease-in-out,' +
    'top 0.38s ease-in-out,' +
    'width 0.38s ease-in-out,' +
    'height 0.38s ease-in-out,' +
    'border-radius 0.55s ease-in,' +
    'box-shadow 0.25s ease';

  protected get backgroundGradient() {
    return `linear-gradient(to right, var(--color-aikiaiki-250), var(--color-aikiaiki-150))`;
  }

  protected get windowContainerClasses() {
    return {
      'opacity-0': this.state() !== 'open' && this.state() !== 'opening',
      'duration-450 delay-200': this.state() === 'opening',
      'duration-400': this.state() === 'closing',
      'pointer-events-none': this.state() !== 'open',
    };
  }

  protected get iconContainerClasses() {
    return { 'opacity-0 pointer-events-none': this.state() === 'open' };
  }

  constructor() {
    toObservable(this.state)
      .pipe(takeUntilDestroyed())
      .subscribe(state => {
        switch (state) {
          case 'opening':
            this.setOpeningStyles();
            break;
          case 'open':
            this.setOpenStyles();
            break;
          case 'closing':
            this.setClosingStyles();
            break;
          case 'closed':
            this.setClosedStyles();
            break;
        }
      });

    this.keyboardService
      .onKeyCodeDown(KeyCode.F_Key)
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (!event.altKey) return;

        if (this.state() === 'open') this.collapseWindow();
        else if (this.state() === 'closed') this.openWindow();
      });

    // track height and width for the content to be able to dynamically render content layout
    toObservable(this._widthStyle)
      .pipe(takeUntilDestroyed())
      .subscribe(width => (this.windowContentService.width = width));
    toObservable(this._heightStyle)
      .pipe(takeUntilDestroyed())
      .subscribe(height => (this.windowContentService.height = height));
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const rect = this._anchor().nativeElement.getBoundingClientRect();
      this._leftStyle.set(`${rect.left}px`);
      this._topStyle.set(`${rect.top}px`);
      this._widthStyle.set(rect.width);
      this._heightStyle.set(rect.height);
    }, 0);
  }

  private setOpeningStyles() {
    this._transitionStyle.set(this._openingTransition);

    this._borderRadiusStyle.set('14px');

    const boxShadow = `var(--shadow-around)`;
    this._boxShadowStyle.set(boxShadow);

    const rect = this._anchor().nativeElement.getBoundingClientRect();
    const openLeft = 12;
    const openTop = Math.max(20, rect.bottom - this.INITIAL_OPEN_HEIGHT);

    this._leftStyle.set(`${openLeft}px`);
    this._topStyle.set(`${openTop}px`);
    this._widthStyle.set(this.INITIAL_OPEN_WIDTH);
    this._heightStyle.set(this.INITIAL_OPEN_HEIGHT);
  }

  private setOpenStyles() {
    this._transitionStyle.set('none');
  }

  private setClosingStyles() {
    this._transitionStyle.set(this._closingTransition);
    this._transitionDelayStyle.set('100ms');

    this._borderRadiusStyle.set('50%');
    this._boxShadowStyle.set('none');

    const rect = this._anchor().nativeElement.getBoundingClientRect();

    this._leftStyle.set(`${rect.left}px`);
    this._topStyle.set(`${rect.top}px`);
    this._widthStyle.set(rect.width);
    this._heightStyle.set(rect.height);
  }

  private setClosedStyles() {
    this._transitionStyle.set('none');
    this._transitionDelayStyle.set('0ms');
  }

  protected openWindow() {
    if (this.state() !== 'closed') return;

    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'width') return;
      el.removeEventListener('transitionend', onEnd);

      this.state.set('open');
      this.opened.emit();
    };

    const el = this._morphEl().nativeElement;
    el.addEventListener('transitionend', onEnd);

    this.state.set('opening');
  }

  protected collapseWindow() {
    if (this.state() !== 'open') return;

    const onEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'width') return;
      el.removeEventListener('transitionend', onEnd);

      this.state.set('closed');
      this.closed.emit();
    };

    const el = this._morphEl().nativeElement;
    el.addEventListener('transitionend', onEnd);
    this.state.set('closing');
  }

  protected onMove(pos: Position): void {
    this._leftStyle.set(`${pos.left}px`);
    this._topStyle.set(`${pos.top}px`);
  }

  protected onResize(bounds: Bounds): void {
    this._leftStyle.set(`${bounds.left}px`);
    this._topStyle.set(`${bounds.top}px`);
    this._widthStyle.set(bounds.w);
    this._heightStyle.set(bounds.h);
  }
}
